import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["innerHTML"]
const _hoisted_2 = ["aria-controls", "aria-label", "disabled"]
const _hoisted_3 = ["aria-controls", "aria-label", "disabled"]
const _hoisted_4 = ["id"]

import { ref, watchEffect, nextTick, computed, onMounted } from "vue";

import { getIcon } from "~/js/composables/icons";
import { getClasses } from "~/js/composables/classes";
import { sanitizeHtml } from "~/js/utils/sanitize-html";

import { Image } from "~/js/interfaces/media";

import TextH3 from "~/vue/atoms/texts/TextH3.vue";
import Body from "~/vue/atoms/texts/TextBody.vue";
import ImageLogo from "~/vue/atoms/images/ImageLogo.vue";

interface Sdg {
	heading: string;
	description: string;
	image: Image;
}

interface A11Y {
	title: string;
	previous: string;
	next: string;
}

interface Props {
	heading: string | null;
	headingColor?: string | null;
	content: string | null;
	sdgs: Array<Sdg>;
	animate?: boolean;
	a11y: A11Y;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'SliderSdgs',
  props: {
    heading: {},
    headingColor: { default: null },
    content: {},
    sdgs: {},
    animate: { type: Boolean, default: false },
    a11y: {}
  },
  setup(__props: any) {

const props = __props;

const classes = ref({
	card: {
		background: "bg-white",
		snap: "snap-end",
		size: "w-[300px] min-h-[300px]",
		layout: "rounded-4xl relative overflow-hidden",
	},
	container: {
		animation: "transition-transform duration-700 ease-in-out",
		flex: "flex shrink-0",
		size: "w-screen",
	},
	controls: {
		flex: "flex items-center justify-end",
		space: "space-x-4",
	},
	dotsContainer: {
		display: "hidden sm:flex",
		space: "space-x-1",
	},
	dots: {
		animation: "transition-all",
		display: "inline-flex",
		background: "bg-viridian-500",
		layout: "rounded-full",
		size: "w-3 h-3",
	},
	header: {
		flex: "flex items-end justify-between",
		margin: "mb-10",
		size: "w-full",
		space: "space-x-2",
	},
	prevnext: {
		background: "bg-viridian-500",
		color: "text-white",
		display: "flex items-center justify-center",
		layout: "rounded-full",
		size: "w-8 h-8",
	},
	prevnextDisabled: {
		background: "bg-concrete-300",
		color: "text-white",
		display: "flex items-center justify-center",
		layout: "rounded-full",
		size: "w-8 h-8",
	},
	scroller: {
		flex: "flex shrink-0",
		oveflow: "scrollbar-hide scroll-smooth snap-mandatory snap-x overflow-x-auto overflow-y-visible",
		size: "w-screen",
	},
	wrapper: {
		flex: "flex justify-center relative",
		position: "relative",
	},
});

const scroller = ref();
const scrollerContainer = ref();
const placeholder = ref(null);
const ghost = ref(null);
const loaded = ref(false);
const scrollPos = ref(0);
const totalScroll = ref(0);
const scrollHidden = ref(true);
const id = ref(Math.floor(Date.now() / 1000));
const sanitizedContent = computed(() => sanitizeHtml(props.content));

const calculateScrollPositions = () => {
	scrollPos.value = scroller.value.scrollLeft;
	totalScroll.value = scrollerContainer.value?.offsetWidth - window.innerWidth;
};

const resize = async () => {
	if (placeholder.value?.getBoundingClientRect()?.left) {
		ghost.value = placeholder.value.getBoundingClientRect().left;
	}

	totalScroll.value = (scrollerContainer.value?.offsetWidth ?? 0) - window.innerWidth;

	// reset scrolling position to 0 when it's first loaded
	if (!loaded.value) {
		// this is needed because the ghost.value is being used in setting the width --> DOM changes needs the nextTick to execute
		await nextTick();

		scroller.value.scroll({
			left: 0,
			top: 0,
		});
	}

	scrollHidden.value = scrollerContainer.value?.offsetWidth > window.innerWidth;
	loaded.value = true;
};

const prevNext = (direction) => {
	const position =
		direction == "prev"
			? scroller.value.scrollLeft - scrollerContainer.value.lastElementChild?.offsetWidth
			: scroller.value.scrollLeft + scrollerContainer.value.lastElementChild?.offsetWidth;

	scroller.value.scroll({
		left: position,
		top: 0,
		behavior: "smooth",
	});
};

watchEffect(() => {
	if (ghost.value && scroller.value) {
		scroller.value.addEventListener("scroll", calculateScrollPositions);
		window.addEventListener("resize", resize);
		calculateScrollPositions();
		resize();
	}
});

watchEffect(() => {
	if (placeholder.value?.getBoundingClientRect().left) {
		ghost.value = placeholder.value.getBoundingClientRect().left;
	}
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref: "container",
    class: _normalizeClass(["w-full", __props.animate ? 'animate-slider' : ''])
  }, [
    _createElementVNode("header", {
      ref_key: "placeholder",
      ref: placeholder,
      class: _normalizeClass(_unref(getClasses)(classes.value, 'header'))
    }, [
      _createElementVNode("div", {
        class: _normalizeClass(["flex flex-col", __props.animate ? 'animate-slider-items' : ''])
      }, [
        (__props.heading)
          ? (_openBlock(), _createBlock(TextH3, {
              key: 0,
              utilities: __props.headingColor ?? undefined
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(__props.heading), 1)
              ]),
              _: 1
            }, 8, ["utilities"]))
          : _createCommentVNode("", true),
        (__props.content)
          ? (_openBlock(), _createBlock(Body, {
              key: 1,
              size: "large"
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", { innerHTML: sanitizedContent.value }, null, 8, _hoisted_1)
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ], 2),
      _createElementVNode("div", {
        class: _normalizeClass([_unref(getClasses)(classes.value, 'controls'), __props.animate ? 'animate-slider-items' : ''])
      }, [
        _createElementVNode("button", {
          "aria-controls": __props.a11y.title,
          "aria-label": __props.a11y.previous,
          disabled: scrollPos.value < 10,
          class: _normalizeClass(scrollPos.value < 10 ? _unref(getClasses)(classes.value, 'prevnextDisabled') : _unref(getClasses)(classes.value, 'prevnext')),
          onClick: _cache[0] || (_cache[0] = () => prevNext('prev'))
        }, [
          _createElementVNode("i", {
            class: _normalizeClass(_unref(getIcon)('chevron-left'))
          }, null, 2)
        ], 10, _hoisted_2),
        _createElementVNode("button", {
          "aria-controls": __props.a11y.title,
          "aria-label": __props.a11y.next,
          disabled: scrollPos.value > totalScroll.value - 20,
          class: _normalizeClass(
						scrollPos.value > totalScroll.value - 20
							? _unref(getClasses)(classes.value, 'prevnextDisabled')
							: _unref(getClasses)(classes.value, 'prevnext')
					),
          onClick: _cache[1] || (_cache[1] = () => prevNext('next'))
        }, [
          _createElementVNode("i", {
            class: _normalizeClass(_unref(getIcon)('chevron-right'))
          }, null, 2)
        ], 10, _hoisted_3)
      ], 2)
    ], 2),
    _createElementVNode("section", {
      ref_key: "scroller",
      ref: scroller,
      class: _normalizeClass([
				'scrollbar-hide relative relative left-1/2 flex w-screen -translate-x-1/2 snap-x gap-6 overflow-x-auto scroll-smooth',
			])
    }, [
      _createElementVNode("ul", {
        id: `slider-${id.value}`,
        ref_key: "scrollerContainer",
        ref: scrollerContainer,
        role: "menubar",
        class: "flex h-full flex-nowrap pr-4"
      }, [
        _createElementVNode("li", {
          "aria-hidden": "true",
          role: "none",
          class: "snap-end",
          style: _normalizeStyle({ width: ghost.value + 'px' })
        }, " ", 4),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.sdgs, (sdg, i) => {
          return (_openBlock(), _createElementBlock("li", {
            key: sdg.heading,
            class: _normalizeClass(["slider-item", [
						_unref(getClasses)(classes.value, 'card'),
						i > 0 ? 'ml-6 lg:ml-8' : '',
						__props.animate ? 'animate-slider-items' : '',
					]]),
            role: "listitem"
          }, [
            (sdg.image.srcset)
              ? (_openBlock(), _createBlock(ImageLogo, {
                  key: 0,
                  image: sdg.image,
                  ratio: "w-auto",
                  utilities: "absolute left-0 top-0 w-full h-full"
                }, null, 8, ["image"]))
              : _createCommentVNode("", true)
          ], 2))
        }), 128))
      ], 8, _hoisted_4)
    ], 512)
  ], 2))
}
}

})